// The ONE dialog. Every modal in the app — the static ones in index.html, the ones
// built here (alert / confirm / prompt), the cloud dialogs — is a `[data-modal]`
// backdrop, and behaves the same through `installDialogKeys`:
//   - Esc clicks its `[data-role="cancel"]` button (or just hides it);
//   - Enter clicks its `[data-role="confirm"]` button, except from a textarea,
//     a select, a focused button or an input marked `data-enter-commits` (a
//     table cell), which keep their own Enter; a focused input is blurred first,
//     so the value being typed commits before the dialog confirms;
//   - a click on the backdrop never discards anything: it nudges the dialog. Only a
//     `data-dismissable` one (nothing to lose, e.g. a change log) closes on it.
// Buttons run Cancel first, the confirming action last.

import { tr } from './i18n';

export interface DialogButton {
  label: string;
  role?: 'cancel' | 'confirm';
  primary?: boolean;
  danger?: boolean;
  onClick: () => void;
}

export interface DialogOptions {
  title?: string;
  message?: string;
  body?: HTMLElement;
  buttons: DialogButton[];
}

/** Open a modal built like the static ones. Labels are English and translated
 *  here; `close()` removes it. */
export function openDialog(opts: DialogOptions): { root: HTMLElement; close: () => void } {
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  backdrop.dataset.modal = '';
  const box = document.createElement('div');
  box.className = 'modal modal-small';
  box.setAttribute('role', 'dialog');
  if (opts.title) { const h = document.createElement('h3'); h.textContent = tr(opts.title); box.appendChild(h); }
  if (opts.message) {
    const p = document.createElement('p');
    p.className = 'modal-message';
    p.textContent = opts.message;
    box.appendChild(p);
  }
  if (opts.body) box.appendChild(opts.body);
  const actions = document.createElement('div');
  actions.className = 'modal-actions';
  for (const b of opts.buttons) {
    const btn = document.createElement('button');
    btn.textContent = tr(b.label);
    btn.className = b.primary ? (b.danger ? 'primary danger' : 'primary') : 'ghost';
    if (b.role) btn.dataset.role = b.role;
    btn.addEventListener('click', b.onClick);
    actions.appendChild(btn);
  }
  box.appendChild(actions);
  backdrop.appendChild(box);
  document.body.appendChild(backdrop);
  const close = (): void => backdrop.remove();
  return { root: backdrop, close };
}

/** Tell the user something; resolves once they close it. `message` is shown as
 *  given — translate it at the call site. */
export function showAlert(message: string, title?: string): Promise<void> {
  return new Promise((resolve) => {
    const d = openDialog({ title, message, buttons: [{ label: 'OK', role: 'confirm', primary: true, onClick: () => { d.close(); resolve(); } }] });
    d.root.dataset.cancelIsConfirm = ''; // Esc on a one-button note just closes it
    focusLater(d.root.querySelector('button'));
  });
}

/** Ask yes / no; resolves true for the confirming button. */
export function showConfirm(message: string, opts: { title?: string; ok?: string; cancel?: string; danger?: boolean } = {}): Promise<boolean> {
  return new Promise((resolve) => {
    const done = (v: boolean): void => { d.close(); resolve(v); };
    const d = openDialog({
      title: opts.title,
      message,
      buttons: [
        { label: opts.cancel ?? 'Cancel', role: 'cancel', onClick: () => done(false) },
        { label: opts.ok ?? 'OK', role: 'confirm', primary: true, danger: opts.danger, onClick: () => done(true) },
      ],
    });
    focusLater(d.root.querySelector('[data-role="confirm"]'));
  });
}

/** Ask for one line of text (or, `multiline`, several); resolves to it, or null
 *  when cancelled. */
export function showPrompt(title: string, label: string, initial = '', ok = 'Save', multiline = false): Promise<string | null> {
  return new Promise((resolve) => {
    const body = document.createElement('div');
    body.className = 'form-row';
    const lab = document.createElement('label'); lab.textContent = tr(label);
    const input = multiline ? document.createElement('textarea') : document.createElement('input');
    if (input instanceof HTMLInputElement) input.type = 'text';
    else input.rows = Math.max(3, initial.split('\n').length + 1);
    input.className = 'dialog-input'; input.value = initial;
    body.append(lab, input);
    const done = (v: string | null): void => { d.close(); resolve(v); };
    const d = openDialog({
      title,
      body,
      buttons: [
        { label: 'Cancel', role: 'cancel', onClick: () => done(null) },
        { label: ok, role: 'confirm', primary: true, onClick: () => done(input.value) },
      ],
    });
    setTimeout(() => { input.focus(); input.select(); }, 0);
  });
}

function focusLater(el: Element | null): void {
  if (el instanceof HTMLElement) setTimeout(() => el.focus(), 0);
}

/** The dialog in front: the last open `[data-modal]` in the document. */
export function topDialog(): HTMLElement | null {
  const open = Array.from(document.querySelectorAll<HTMLElement>('[data-modal]')).filter((m) => !m.classList.contains('hidden') && m.isConnected);
  return open[open.length - 1] ?? null;
}

function cancelDialog(m: HTMLElement): void {
  const btn = m.querySelector<HTMLButtonElement>('[data-role="cancel"]')
    ?? (m.dataset.cancelIsConfirm != null ? m.querySelector<HTMLButtonElement>('[data-role="confirm"]') : null);
  if (btn) btn.click();
  else m.classList.add('hidden');
}

/** Esc / Enter / backdrop behaviour for every dialog (installed once, at startup).
 *  Runs in the capture phase so a dialog's keys never reach the drawing. */
export function installDialogKeys(): void {
  window.addEventListener('keydown', (e) => {
    const m = topDialog();
    if (!m) return;
    const t = e.target as HTMLElement | null;
    if (e.key === 'Escape') {
      e.preventDefault(); e.stopPropagation();
      cancelDialog(m);
    } else if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
      const tag = t?.tagName;
      if (tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON' || t?.dataset.enterCommits != null) return;
      const ok = m.querySelector<HTMLButtonElement>('[data-role="confirm"]');
      if (!ok || ok.disabled) return;
      e.preventDefault(); e.stopPropagation();
      if (tag === 'INPUT') t!.blur();
      ok.click();
    }
  }, true);
  document.addEventListener('mousedown', (e) => {
    const m = topDialog();
    if (!m || e.target !== m) return;
    if (m.dataset.dismissable != null) { cancelDialog(m); return; }
    const box = m.firstElementChild as HTMLElement | null;
    if (!box) return;
    box.classList.remove('modal-nudge');
    void box.offsetWidth; // restart the animation
    box.classList.add('modal-nudge');
  }, true);
}
